import { useCallback, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Button } from '../../../_core/button/button.component';
import { IButtonDropdownOptionProps } from '../../../_core/button/button-dropdown-option-component';
import { useRemoveInvite, useResendInvite } from '../../../domains/invite/invite.service';
import { useRemoveMember, useRestoreMember } from '../../../domains/member/member.service';
import { IDepartment, IInviteOverview, ILocation, IMemberOverview, ROLE } from '../../../../lib/types';
import { showError } from '../../../../lib/utils';
import { NullableStaff } from './staff-table.types';
import { IAdminProps } from '../../../_core/core.types';
import { RIDialog } from '../../../_core/dialog/dialog.component';
import { EditMemberContainer } from '../../../domains/member/edit/edit-member-form.container';
import { EditInviteContainer } from '../../../domains/invite/edit/edit-invite-form.container';
import { IPermanent } from '../../../domains/request-group/request-group.types';
import { useConfirm } from '../../../_core/confirm/confirm.utils';

export interface IStaffActionProps {
  invite?: IInviteOverview;
  member?: IMemberOverview;
}

export interface IStaffActionButtonProps extends IStaffActionProps, IAdminProps {
  currUserId: string;
  departments?: IDepartment[];
  locations?: ILocation[];
  setShowEditDepartmentsModal: React.Dispatch<React.SetStateAction<NullableStaff>>;
  setShowEditLocationsModal: React.Dispatch<React.SetStateAction<NullableStaff>>;
  setShowEditRolesModal: React.Dispatch<React.SetStateAction<NullableStaff>>;
}

export const StaffActionButton: React.FC<IStaffActionButtonProps> = ({
  currUserId,
  departments,
  invite,
  isAdmin,
  locations,
  member,
  setShowEditDepartmentsModal,
  setShowEditLocationsModal,
  setShowEditRolesModal
}) => {
  const { removeInvite, loading: removingInvite } = useRemoveInvite();
  const { removeMember, loading: removingMember } = useRemoveMember();
  const { restoreMember, loading: restoringMember } = useRestoreMember();
  const { resendInvite, loading: resendingInvite } = useResendInvite();

  const [editing, setEditing] = useState(false);

  const { ConfirmationDialog: PermanentlyDeleteConfirmationDialog, confirm: confirmPermanent } = useConfirm({
    title: 'Are you sure you want to permanently delete this staff member?',
    message: 'This action cannot be undone. Consider archiving instead.'
  });

  const editAction: IButtonDropdownOptionProps = useMemo(
    () => ({ onClick: () => setEditing(true), text: 'Edit Name' }),
    []
  );

  const editStaffDepartmentsAction: IButtonDropdownOptionProps = useMemo(
    () => ({
      disabled: !departments?.length,
      onClick: () => setShowEditDepartmentsModal(invite ?? member ?? null),
      text: 'Edit Departments'
    }),
    [departments?.length, invite, member, setShowEditDepartmentsModal]
  );

  const editStaffLocationsAction: IButtonDropdownOptionProps = useMemo(
    () => ({
      disabled: !locations?.length,
      onClick: () => setShowEditLocationsModal(invite ?? member ?? null),
      text: 'Edit Locations'
    }),
    [invite, locations?.length, member, setShowEditLocationsModal]
  );

  const editStaffRolesAction: IButtonDropdownOptionProps = useMemo(
    () => ({ onClick: () => setShowEditRolesModal(invite ?? member ?? null), text: 'Edit Roles' }),
    [invite, member, setShowEditRolesModal]
  );

  const onResendInvite = useCallback(async () => {
    if (invite)
      try {
        const result = await resendInvite(invite);

        if (!result.data?.resendInvite.sentAt) toast.error('Unable to deliver invite');
        else toast.success(`Resent invite`);
      } catch (err) {
        showError('Unable to resend invite', err as Error);
      }
  }, [invite, resendInvite]);

  const resendInviteAction: IButtonDropdownOptionProps = useMemo(
    () => ({ onClick: onResendInvite, text: 'Resend Invite' }),
    [onResendInvite]
  );

  const onRestoreMember = useCallback(async () => {
    if (member?.deletedAt)
      try {
        await restoreMember(member);
        toast.success(`Restored member`);
      } catch (err) {
        showError('Unable to restore member', err as Error);
      }
  }, [member, restoreMember]);

  const restoreMemberAction: IButtonDropdownOptionProps = useMemo(
    () => ({ onClick: onRestoreMember, text: 'Restore' }),
    [onRestoreMember]
  );

  const onDeleteStaff = useCallback(
    async (variables?: IPermanent) => {
      const action = variables?.permanent ? 'Delete' : 'Archive';
      if (invite)
        try {
          await removeInvite({ _id: invite._id, ...variables });
          toast.success(`${action}d invite`);
        } catch (err) {
          showError(`Unable to ${action.toLowerCase()} invite`, err as Error);
        }
      else if (member)
        try {
          await removeMember({ _id: member._id, ...variables });
          toast.success(`${action}d member`);
        } catch (err) {
          showError(`Unable to ${action.toLowerCase()} member`, err as Error);
        }
    },
    [invite, member, removeInvite, removeMember]
  );

  const archiveStaffAction: IButtonDropdownOptionProps = useMemo(
    () => ({ onClick: () => onDeleteStaff(), text: 'Archive' }),
    [onDeleteStaff]
  );

  const deleteStaffAction: IButtonDropdownOptionProps = useMemo(
    () => ({
      onClick: () =>
        confirmPermanent().then((confirmed) => {
          if (confirmed) onDeleteStaff({ permanent: true });
        }),
      text: 'Delete'
    }),
    [confirmPermanent, onDeleteStaff]
  );

  const options = useMemo(() => {
    const newOptions: IButtonDropdownOptionProps[] = [editAction, editStaffDepartmentsAction, editStaffLocationsAction];

    const memberIsOwner = member?.roles.includes(ROLE.COMPANY_OWNER);
    if (isAdmin && !memberIsOwner) newOptions.push(editStaffRolesAction);
    if (invite) newOptions.push(resendInviteAction);
    if (member?.deletedAt) newOptions.push(restoreMemberAction);
    if (!memberIsOwner) {
      if (isAdmin && (!member || (member.user._id !== currUserId && !member.deletedAt)))
        newOptions.push(archiveStaffAction);

      newOptions.push(deleteStaffAction);
    }

    return newOptions;
  }, [
    editAction,
    editStaffDepartmentsAction,
    editStaffLocationsAction,
    isAdmin,
    editStaffRolesAction,
    invite,
    resendInviteAction,
    member,
    restoreMemberAction,
    currUserId,
    archiveStaffAction,
    deleteStaffAction
  ]);

  return (
    <>
      <Button
        slim
        dropdown={{ options }}
        text="ACTION"
        size="xl"
        loading={removingMember || restoringMember || removingInvite || resendingInvite}
      />
      <RIDialog open={editing} setOpen={setEditing}>
        {!!invite && <EditInviteContainer invite={invite} close={() => setEditing(false)} />}
        {!!member && <EditMemberContainer member={member} close={() => setEditing(false)} />}
      </RIDialog>
      <PermanentlyDeleteConfirmationDialog />
    </>
  );
};
